import type React from 'react';
import {Box, IconButton, Link, Tooltip, Typography, useMediaQuery} from '@mui/material';
import {ChevronLeft} from '@mui/icons-material';
import type {Theme} from '@mui/material/styles';
import {CustomBreadcrumbs} from '~/components/CustomBreadcrumbs/CustomBreadcrumbs';
import {breadcrumb} from '../domain/exploreTree';
import {LOCALE} from '~/pages/category-profile/domain/locale';

type Props = {selected: string; count: number; onSelect: (path: string) => void};

const ONE_LINE = {whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block'} as const;

/**
 * The scope of the table as a breadcrumb ("All categories › L1 › L2", spec 3.4) and its row count, on the left of the
 * table's toolbar. Always one line: middle levels fold into "…" (click to show them), long names end in an ellipsis
 * with the full name on hover, and the current level keeps the most room. On phones: "‹" up a level and the current
 * category only.
 */
export const ExploreBreadcrumb: React.FC<Props> = ({selected, count, onSelect}) => {
    const narrow = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const phone = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const crumbs = [{label: 'All categories', path: ''}, ...breadcrumb(selected)];
    const last = crumbs.length - 1;
    const countText = phone ? `· ${count.toLocaleString(LOCALE)}` : `· ${count.toLocaleString(LOCALE)} ${count === 1 ? 'category' : 'categories'}`;

    if (phone) {
        // Too little room for a trail: "‹" up one level, then the current category and the count.
        const parent = last > 0 ? crumbs[last - 1] : null;
        return (
            <Box component="nav" aria-label="Category path" sx={{display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: '1 1 auto', pl: 0.5}}>
                {parent && (
                    <Tooltip title={`Up to ${parent.label}`}>
                        <IconButton size="small" aria-label={`Up to ${parent.label}`} onClick={() => onSelect(parent.path)} sx={{ml: -0.5}}>
                            <ChevronLeft fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
                <Typography component="span" aria-current="page" variant="body2" sx={{...ONE_LINE, fontWeight: 600, minWidth: 0}}>
                    {crumbs[last].label}
                </Typography>
                <Typography component="span" variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap', flexShrink: 0}}
                            aria-label={`${count} ${count === 1 ? 'category' : 'categories'}`}>
                    {countText}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: '1 1 auto', overflow: 'hidden', pl: 0.5}}>
            <CustomBreadcrumbs aria-label="Category path" separatorType="slash" separator="›" maxItems={narrow ? 3 : 6} itemsBeforeCollapse={1}
                               itemsAfterCollapse={1}
                               sx={{
                                   minWidth: 0,
                                   '& .MuiBreadcrumbs-ol': {flexWrap: 'nowrap'},
                                   '& .MuiBreadcrumbs-li': {minWidth: 0, flexShrink: 1, overflow: 'hidden', display: 'flex'},
                                   '& .MuiBreadcrumbs-li:last-of-type': {flexShrink: 0.2},
                                   '& .MuiBreadcrumbs-separator': {mx: 0.5, flexShrink: 0},
                               }}>
                {crumbs.map((c, i) => (
                    <Tooltip key={c.path || 'all'} title={c.label} enterDelay={600} disableInteractive>
                        {i === last ? (
                            <Typography component="span" aria-current="page" variant="body2" sx={{...ONE_LINE, fontWeight: 600, color: 'text.primary'}}>
                                {c.label}
                            </Typography>
                        ) : (
                            <Link component="button" variant="body2" underline="hover" onClick={() => onSelect(c.path)}
                                  sx={{...ONE_LINE, maxWidth: {xs: 120, md: 180}, textAlign: 'left'}}>
                                {c.label}
                            </Link>
                        )}
                    </Tooltip>
                ))}
            </CustomBreadcrumbs>
            <Typography component="span" variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap', flexShrink: 0}}>
                {countText}
            </Typography>
        </Box>
    );
};
